import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { LogOut, Download, Search, Eye, Mail, Phone, MapPin, Calendar, Home as HomeIcon, MessageCircle, CheckCircle, FileText, Plus, Trash2, Pencil, X, Users, Clock, AlertCircle, UserPlus, History, Bell, BarChart3, TrendingUp, MousePointer, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://realestate-stats.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

// Password simple (puedes cambiarlo después)
const ADMIN_PASSWORD = 'remax2026';

// Constants for CRM
const CLIENT_STATUS_OPTIONS = [
  { value: 'prospecto', label: 'Prospecto', color: 'bg-blue-100 text-blue-800' },
  { value: 'activo', label: 'Activo', color: 'bg-green-100 text-green-800' },
  { value: 'negociando', label: 'Negociando', color: 'bg-amber-100 text-amber-800' },
  { value: 'cerrado_ganado', label: 'Cerrado (Ganado)', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'cerrado_perdido', label: 'Cerrado (Perdido)', color: 'bg-red-100 text-red-800' },
  { value: 'inactivo', label: 'Inactivo', color: 'bg-slate-100 text-slate-800' },
];

const CLIENT_INTEREST_OPTIONS = [
  { value: 'compra', label: 'Compra' },
  { value: 'venta', label: 'Venta' },
  { value: 'alquiler', label: 'Alquiler' },
  { value: 'inversion', label: 'Inversión' },
  { value: 'tasacion', label: 'Tasación' },
];

const FOLLOW_UP_OPTIONS = [
  { value: '1_semana', label: '1 Semana' },
  { value: '2_semanas', label: '2 Semanas' },
  { value: '1_mes', label: '1 Mes' },
  { value: '2_meses', label: '2 Meses' },
  { value: '3_meses', label: '3 Meses' },
  { value: '6_meses', label: '6 Meses' },
  { value: 'sin_seguimiento', label: 'Sin Seguimiento' },
];

const INTERACTION_TYPES = [
  { value: 'llamada', label: 'Llamada' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'reunion', label: 'Reunión' },
  { value: 'visita', label: 'Visita de Propiedad' },
];

const INTERACTION_OUTCOMES = [
  { value: 'interesado', label: 'Interesado' },
  { value: 'no_interesado', label: 'No Interesado' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'cerrado', label: 'Cerrado' },
];

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [appraisals, setAppraisals] = useState([]);
  const [filteredAppraisals, setFilteredAppraisals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [articles, setArticles] = useState([]);
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    excerpt: '',
    image_url: ''
  });
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleDetail, setShowArticleDetail] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // CRM State
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientStats, setClientStats] = useState(null);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [showCreateClientDialog, setShowCreateClientDialog] = useState(false);
  const [showClientDetailDialog, setShowClientDetailDialog] = useState(false);
  const [showEditClientDialog, setShowEditClientDialog] = useState(false);
  const [showAddInteractionDialog, setShowAddInteractionDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientStatusFilter, setClientStatusFilter] = useState('all');
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    email: '',
    interest: 'compra',
    property_type: '',
    neighborhood: '',
    budget_min: '',
    budget_max: '',
    property_size: '',
    follow_up_frequency: '1_mes',
    notes: ''
  });
  const [newInteraction, setNewInteraction] = useState({
    type: 'llamada',
    notes: '',
    outcome: 'pendiente'
  });

  // Subscriptions State
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');

  // Analytics State (initialized from localStorage cache for instant first paint)
  const adminCache = (() => {
    try {
      const raw = localStorage.getItem('admin_analytics_cache_v1');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [analyticsStats, setAnalyticsStats] = useState(adminCache?.summary || null);
  const [dailyStats, setDailyStats] = useState(adminCache?.daily || []);
  const [pageStats, setPageStats] = useState(adminCache?.pages || []);
  const [searchStats, setSearchStats] = useState(adminCache?.searches || { top_zones: [], top_types: [] });
  const [statsLoading, setStatsLoading] = useState(false);

  const formatText = (format, isEdit = false) => {
    const textareaId = isEdit ? 'edit-article-content' : 'article-content';
    const textarea = document.getElementById(textareaId);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    if (!selectedText) {
      alert('Por favor selecciona el texto que quieres formatear');
      return;
    }

    let formattedText = '';
    switch(format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'h1':
        formattedText = `<h1>${selectedText}</h1>`;
        break;
      case 'h2':
        formattedText = `<h2>${selectedText}</h2>`;
        break;
      case 'h3':
        formattedText = `<h3>${selectedText}</h3>`;
        break;
      case 'p':
        formattedText = `<p>${selectedText}</p>`;
        break;
      case 'link':
        const url = prompt('Ingresa la URL:');
        if (url) formattedText = `<a href="${url}">${selectedText}</a>`;
        else return;
        break;
      default:
        formattedText = selectedText;
    }

    const newContent = 
      textarea.value.substring(0, start) + 
      formattedText + 
      textarea.value.substring(end);
    
    if (isEdit) {
      setEditingArticle({...editingArticle, content: newContent});
    } else {
      setNewArticle({...newArticle, content: newContent});
    }
  };

  useEffect(() => {
    // Check if already logged in
    const loggedIn = sessionStorage.getItem('adminAuth');
    if (loggedIn === 'true') {
      setIsAuthenticated(true);
      fetchAppraisals();
      fetchArticles();
      fetchClients();
      fetchClientStats();
      fetchPendingContacts();
      fetchSubscriptions();
    }
  }, []);

  useEffect(() => {
    // Filter appraisals based on search term
    if (searchTerm) {
      const filtered = appraisals.filter(app => 
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phone.includes(searchTerm) ||
        app.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAppraisals(filtered);
    } else {
      setFilteredAppraisals(appraisals);
    }
  }, [searchTerm, appraisals]);

  // Filter clients based on search and status
  useEffect(() => {
    let filtered = clients;
    
    if (clientSearchTerm) {
      filtered = filtered.filter(client => 
        client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
        (client.email && client.email.toLowerCase().includes(clientSearchTerm.toLowerCase())) ||
        (client.phone && client.phone.includes(clientSearchTerm)) ||
        (client.neighborhood && client.neighborhood.toLowerCase().includes(clientSearchTerm.toLowerCase()))
      );
    }
    
    if (clientStatusFilter !== 'all') {
      filtered = filtered.filter(client => client.status === clientStatusFilter);
    }
    
    setFilteredClients(filtered);
  }, [clientSearchTerm, clientStatusFilter, clients]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      fetchAppraisals();
      fetchArticles();
      fetchClients();
      fetchClientStats();
      fetchPendingContacts();
      fetchSubscriptions();
      toast.success('Bienvenido al panel de administración');
    } else {
      toast.error('Contraseña incorrecta');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    setPassword('');
    toast.success('Sesión cerrada');
  };

  const fetchAppraisals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appraisals`);
      if (response.data.success) {
        setAppraisals(response.data.data);
        setFilteredAppraisals(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching appraisals:', error);
      toast.error('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  };

  // CRM Functions
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      if (response.data.success) {
        setClients(response.data.data);
        setFilteredClients(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Error al cargar los clientes');
    }
  };

  const fetchClientStats = async () => {
    try {
      const response = await axios.get(`${API}/clients/stats/summary`);
      if (response.data.success) {
        setClientStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching client stats:', error);
    }
  };

  const fetchPendingContacts = async () => {
    try {
      const response = await axios.get(`${API}/clients/pending`);
      if (response.data.success) {
        setPendingContacts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pending contacts:', error);
    }
  };

  // Subscriptions Functions
  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions`);
      if (response.data.success) {
        setSubscriptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  // Analytics Functions
  const fetchAnalytics = async () => {
    setStatsLoading(true);
    try {
      const [summaryRes, dailyRes, pagesRes, searchRes] = await Promise.all([
        axios.get(`${API}/analytics/stats/summary`),
        axios.get(`${API}/analytics/stats/daily?days=30`),
        axios.get(`${API}/analytics/stats/pages?days=30`),
        axios.get(`${API}/analytics/stats/searches?days=30`)
      ]);

      const summary = summaryRes.data.success ? summaryRes.data : null;
      const daily = dailyRes.data.success ? (dailyRes.data.stats || []) : [];
      const pages = pagesRes.data.success ? (pagesRes.data.pages || []) : [];
      const searches = searchRes.data.success
        ? { top_zones: searchRes.data.top_zones || [], top_types: searchRes.data.top_types || [] }
        : { top_zones: [], top_types: [] };

      if (summary) setAnalyticsStats(summary);
      setDailyStats(daily);
      setPageStats(pages);
      setSearchStats(searches);

      // Persist for instant first paint on next visit
      try {
        localStorage.setItem('admin_analytics_cache_v1', JSON.stringify({
          summary, daily, pages, searches, ts: Date.now(),
        }));
      } catch { /* quota — ignore */ }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error al cargar las estadísticas');
    } finally {
      setStatsLoading(false);
    }
  };

  const deleteSubscription = async (subscriptionId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta suscripción?')) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/subscriptions/alerts/${subscriptionId}`);
      if (response.data.success) {
        toast.success('Suscripción eliminada');
        fetchSubscriptions();
      }
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Error al eliminar la suscripción');
    }
  };

  const getFilteredSubscriptions = () => {
    if (subscriptionFilter === 'all') return subscriptions;
    return subscriptions.filter(s => s.subscription_type === subscriptionFilter);
  };

  const createClient = async (e) => {
    e.preventDefault();
    try {
      const clientData = {
        ...newClient,
        budget_min: newClient.budget_min ? parseFloat(newClient.budget_min) : null,
        budget_max: newClient.budget_max ? parseFloat(newClient.budget_max) : null,
      };
      
      const response = await axios.post(`${API}/clients`, clientData);
      if (response.data.success) {
        toast.success('Cliente creado exitosamente');
        setShowCreateClientDialog(false);
        setNewClient({
          name: '',
          phone: '',
          email: '',
          interest: 'compra',
          property_type: '',
          neighborhood: '',
          budget_min: '',
          budget_max: '',
          property_size: '',
          follow_up_frequency: '1_mes',
          notes: ''
        });
        fetchClients();
        fetchClientStats();
        fetchPendingContacts();
      }
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error('Error al crear el cliente');
    }
  };

  const fetchClientDetail = async (clientId) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}`);
      if (response.data.success) {
        setSelectedClient(response.data.data);
        setShowClientDetailDialog(true);
      }
    } catch (error) {
      console.error('Error fetching client detail:', error);
      toast.error('Error al cargar el detalle del cliente');
    }
  };

  const updateClient = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    
    try {
      const updateData = {
        name: selectedClient.name,
        phone: selectedClient.phone,
        email: selectedClient.email,
        interest: selectedClient.interest,
        property_type: selectedClient.property_type,
        neighborhood: selectedClient.neighborhood,
        budget_min: selectedClient.budget_min ? parseFloat(selectedClient.budget_min) : null,
        budget_max: selectedClient.budget_max ? parseFloat(selectedClient.budget_max) : null,
        property_size: selectedClient.property_size,
        status: selectedClient.status,
        follow_up_frequency: selectedClient.follow_up_frequency,
        notes: selectedClient.notes
      };
      
      const response = await axios.put(`${API}/clients/${selectedClient.id}`, updateData);
      if (response.data.success) {
        toast.success('Cliente actualizado exitosamente');
        setShowEditClientDialog(false);
        fetchClients();
        fetchClientStats();
        fetchPendingContacts();
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Error al actualizar el cliente');
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/clients/${clientId}`);
      if (response.data.success) {
        toast.success('Cliente eliminado correctamente');
        setShowClientDetailDialog(false);
        fetchClients();
        fetchClientStats();
        fetchPendingContacts();
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Error al eliminar el cliente');
    }
  };

  const addInteraction = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    
    try {
      const response = await axios.post(`${API}/clients/${selectedClient.id}/interactions`, {
        client_id: selectedClient.id,
        type: newInteraction.type,
        notes: newInteraction.notes,
        outcome: newInteraction.outcome
      });
      
      if (response.data.success) {
        toast.success('Interacción registrada exitosamente');
        setShowAddInteractionDialog(false);
        setNewInteraction({ type: 'llamada', notes: '', outcome: 'pendiente' });
        // Refresh client detail
        fetchClientDetail(selectedClient.id);
        fetchClients();
        fetchClientStats();
        fetchPendingContacts();
      }
    } catch (error) {
      console.error('Error adding interaction:', error);
      toast.error('Error al registrar la interacción');
    }
  };

  const markAsContacted = async (clientId) => {
    try {
      const response = await axios.post(`${API}/clients/${clientId}/mark-contacted`);
      if (response.data.success) {
        toast.success('Cliente marcado como contactado');
        fetchClients();
        fetchClientStats();
        fetchPendingContacts();
        if (selectedClient && selectedClient.id === clientId) {
          fetchClientDetail(clientId);
        }
      }
    } catch (error) {
      console.error('Error marking as contacted:', error);
      toast.error('Error al marcar como contactado');
    }
  };

  const sendClientWhatsApp = (client) => {
    const firstName = client.name.split(' ')[0];
    const message = `Hola ${firstName}, te contactamos desde Vendetucasa.ar por tu consulta. ¿Tenés un momento para charlar?`;
    const phone = client.phone ? client.phone.replace(/[^0-9]/g, '') : '';
    if (!phone) {
      toast.error('El cliente no tiene teléfono registrado');
      return;
    }
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getStatusBadge = (status) => {
    const statusOption = CLIENT_STATUS_OPTIONS.find(s => s.value === status);
    if (!statusOption) return <Badge variant="outline">{status}</Badge>;
    return <Badge className={statusOption.color}>{statusOption.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysUntilContact = (nextContactDate) => {
    if (!nextContactDate) return null;
    const today = new Date();
    const contact = new Date(nextContactDate);
    const diffTime = contact - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const exportToCSV = () => {
    const headers = ['Fecha', 'Nombre', 'Email', 'Teléfono', 'Dirección', 'Tipo', 'Tamaño', 'Motivo'];
    const csvData = filteredAppraisals.map(app => [
      new Date(app.createdAt).toLocaleDateString('es-AR'),
      app.name,
      app.email,
      app.phone,
      app.address,
      app.propertyType,
      app.size || '-',
      app.reason || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `solicitudes_tasacion_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Archivo CSV descargado');
  };

  const exportClientsToCSV = () => {
    const headers = ['Nombre', 'Email', 'Teléfono', 'Interés', 'Barrio', 'Estado', 'Fuente', 'Próximo Contacto', 'Fecha Creación'];
    const csvData = filteredClients.map(client => [
      client.name,
      client.email || '-',
      client.phone || '-',
      CLIENT_INTEREST_OPTIONS.find(i => i.value === client.interest)?.label || client.interest,
      client.neighborhood || '-',
      CLIENT_STATUS_OPTIONS.find(s => s.value === client.status)?.label || client.status,
      client.source || '-',
      formatDate(client.next_contact_date),
      formatDate(client.created_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `clientes_crm_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Archivo CSV descargado');
  };

  const viewDetail = (appraisal) => {
    setSelectedAppraisal(appraisal);
    setShowDetail(true);
  };

  const getPropertyTypeLabel = (type) => {
    const types = {
      casa: 'Casa',
      departamento: 'Departamento',
      terreno: 'Terreno',
      local: 'Local Comercial',
      oficina: 'Oficina',
      otro: 'Otro'
    };
    return types[type] || type;
  };

  const getFirstName = (fullName) => {
    return fullName.split(' ')[0];
  };

  const getStreetName = (address) => {
    const match = address.match(/^([^0-9,]+)/);
    return match ? match[1].trim() : address;
  };

  const sendWhatsApp = (appraisal) => {
    const firstName = getFirstName(appraisal.name);
    const streetName = getStreetName(appraisal.address);
    const message = `Hola ${firstName}, te contactamos desde Vendetucasa.ar por tu consulta sobre la propiedad de la calle ${streetName}. ¿Tenés un momento para charlar?`;
    const phone = appraisal.phone.replace(/[^0-9]/g, '');
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const updateStatus = async (appraisalId, newStatus) => {
    try {
      const response = await axios.patch(`${API}/appraisals/${appraisalId}/status?status=${newStatus}`);
      if (response.data.success) {
        toast.success('Estado actualizado correctamente');
        fetchAppraisals();
        setShowDetail(false);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      if (response.data.success) {
        setArticles(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const createArticle = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/articles`, newArticle);
      if (response.data.success) {
        toast.success('Artículo creado exitosamente');
        setNewArticle({ title: '', content: '', excerpt: '', image_url: '' });
        fetchArticles();
      }
    } catch (error) {
      console.error('Error creating article:', error);
      toast.error('Error al crear el artículo');
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este artículo?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/articles/${articleId}`);
      if (response.data.success) {
        toast.success('Artículo eliminado correctamente');
        fetchArticles();
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Error al eliminar el artículo');
    }
  };

  const viewArticleDetail = (article) => {
    setSelectedArticle(article);
    setShowArticleDetail(true);
  };

  const startEditArticle = (article) => {
    setEditingArticle({
      id: article.id,
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || '',
      image_url: article.image_url || ''
    });
    setShowEditDialog(true);
  };

  const updateArticle = async (e) => {
    e.preventDefault();
    if (!editingArticle) return;
    
    try {
      const response = await axios.put(`${API}/articles/${editingArticle.id}`, {
        title: editingArticle.title,
        content: editingArticle.content,
        excerpt: editingArticle.excerpt,
        image_url: editingArticle.image_url
      });
      if (response.data.success) {
        toast.success('Artículo actualizado correctamente');
        setShowEditDialog(false);
        setEditingArticle(null);
        fetchArticles();
      }
    } catch (error) {
      console.error('Error updating article:', error);
      toast.error('Error al actualizar el artículo');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003DA5] to-[#0051D5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="admin-login-card">
          <CardHeader className="text-center">
            <Link to="/" className="flex items-center justify-center space-x-2 mb-4">
              <div className="relative">
                <svg viewBox="0 0 100 100" className="w-14 h-14">
                  <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                  <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                  <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                  <circle cx="25" cy="25" r="5" fill="white"/>
                </svg>
              </div>
              <span className="text-2xl font-bold">
                <span className="text-[#E31C23]">vende</span>
                <span className="text-slate-600">tu</span>
                <span className="text-[#E31C23]">casa</span>
                <span className="text-[#003DA5]">.ar</span>
              </span>
            </Link>
            <CardTitle className="text-2xl text-[#003DA5]">Panel de Administración</CardTitle>
            <CardDescription>Vendetucasa.ar</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  className="mt-1"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-[#E31C23] hover:bg-[#C11A1F] text-white" data-testid="admin-login-button">
                Iniciar Sesión
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-2">
              <div className="relative">
                <svg viewBox="0 0 100 100" className="w-10 h-10">
                  <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                  <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                  <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                  <circle cx="25" cy="25" r="5" fill="white"/>
                </svg>
              </div>
              <span className="text-xl font-bold">
                <span className="text-[#E31C23]">vende</span>
                <span className="text-slate-600">tu</span>
                <span className="text-[#E31C23]">casa</span>
                <span className="text-[#003DA5]">.ar</span>
              </span>
            </Link>
            <div className="border-l pl-3 ml-2">
              <h1 className="text-lg font-bold text-[#003DA5]">Panel de Administración</h1>
              <p className="text-xs text-slate-600">Panel Vendetucasa.ar</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-[#E31C23] text-[#E31C23] hover:bg-red-50" data-testid="admin-logout-button">
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="clients" className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-5 mb-8">
            <TabsTrigger value="clients" data-testid="tab-clients">
              <Users className="w-4 h-4 mr-2" />
              Clientes
            </TabsTrigger>
            <TabsTrigger value="statistics" data-testid="tab-statistics" onClick={fetchAnalytics}>
              <BarChart3 className="w-4 h-4 mr-2" />
              Estadísticas
            </TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
              <Bell className="w-4 h-4 mr-2" />
              Suscripciones
            </TabsTrigger>
            <TabsTrigger value="appraisals" data-testid="tab-appraisals">
              <HomeIcon className="w-4 h-4 mr-2" />
              Solicitudes
            </TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles">
              <FileText className="w-4 h-4 mr-2" />
              Artículos
            </TabsTrigger>
          </TabsList>

          {/* Clients Tab (CRM) */}
          <TabsContent value="clients" data-testid="clients-tab-content">
            {/* CRM Stats Cards */}
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Clientes</CardTitle>
                  <Users className="h-4 w-4 text-[#003DA5]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#003DA5]">{clientStats?.total_clients || 0}</div>
                  <p className="text-xs text-slate-600">En tu base de datos</p>
                </CardContent>
              </Card>

              <Card className={pendingContacts.length > 0 ? 'border-amber-400 bg-amber-50' : ''}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pendientes de Contacto</CardTitle>
                  <Bell className="h-4 w-4 text-amber-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-amber-600">{clientStats?.pending_contacts || 0}</div>
                  <p className="text-xs text-slate-600">Requieren seguimiento</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Nuevos Este Mes</CardTitle>
                  <UserPlus className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{clientStats?.new_this_month || 0}</div>
                  <p className="text-xs text-slate-600">Clientes agregados</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Activos</CardTitle>
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-emerald-600">{clientStats?.by_status?.activo || 0}</div>
                  <p className="text-xs text-slate-600">En proceso</p>
                </CardContent>
              </Card>
            </div>

            {/* Pending Contacts Alert */}
            {pendingContacts.length > 0 && (
              <Card className="mb-6 border-amber-400 bg-amber-50">
                <CardHeader>
                  <CardTitle className="text-amber-800 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Clientes Pendientes de Contacto ({pendingContacts.length})
                  </CardTitle>
                  <CardDescription className="text-amber-700">
                    Estos clientes necesitan seguimiento urgente
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {pendingContacts.slice(0, 6).map(client => (
                      <div key={client.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-200">
                        <div>
                          <p className="font-medium text-slate-900">{client.name}</p>
                          <p className="text-sm text-amber-700">
                            {client.days_overdue > 0 ? `${client.days_overdue} días de atraso` : 'Contactar hoy'}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => sendClientWhatsApp(client)} className="text-green-600" title="WhatsApp">
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => markAsContacted(client.id)} className="text-blue-600" title="Marcar contactado">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions Bar */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                  <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                        placeholder="Buscar por nombre, email, teléfono o barrio..."
                        value={clientSearchTerm}
                        onChange={(e) => setClientSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="client-search-input"
                      />
                    </div>
                    <Select value={clientStatusFilter} onValueChange={setClientStatusFilter}>
                      <SelectTrigger className="w-full md:w-48" data-testid="client-status-filter">
                        <SelectValue placeholder="Filtrar por estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los estados</SelectItem>
                        {CLIENT_STATUS_OPTIONS.map(status => (
                          <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { fetchClients(); fetchClientStats(); fetchPendingContacts(); }} variant="outline">
                      Actualizar
                    </Button>
                    <Button onClick={exportClientsToCSV} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50">
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button onClick={() => setShowCreateClientDialog(true)} className="bg-[#E31C23] hover:bg-[#C11A1F] text-white" data-testid="create-client-button">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Nuevo Cliente
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Clients Table */}
            <Card>
              <CardHeader>
                <CardTitle>Lista de Clientes</CardTitle>
                <CardDescription>
                  {filteredClients.length} de {clients.length} clientes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredClients.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No hay clientes para mostrar</p>
                    <Button onClick={() => setShowCreateClientDialog(true)} variant="outline" className="mt-4">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Agregar tu primer cliente
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Contacto</TableHead>
                          <TableHead>Interés</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Próximo Contacto</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredClients.map((client) => {
                          const daysUntil = getDaysUntilContact(client.next_contact_date);
                          const isOverdue = daysUntil !== null && daysUntil < 0;
                          const isDueToday = daysUntil === 0;
                          
                          return (
                            <TableRow key={client.id} className={isOverdue ? 'bg-amber-50' : ''} data-testid={`client-row-${client.id}`}>
                              <TableCell>
                                <div className="font-medium">{client.name}</div>
                                <div className="text-sm text-slate-500">{client.neighborhood || '-'}</div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">{client.phone || '-'}</div>
                                <div className="text-sm text-slate-500">{client.email || '-'}</div>
                              </TableCell>
                              <TableCell>
                                {CLIENT_INTEREST_OPTIONS.find(i => i.value === client.interest)?.label || client.interest}
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(client.status)}
                              </TableCell>
                              <TableCell>
                                {client.next_contact_date ? (
                                  <div className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : isDueToday ? 'text-amber-600 font-medium' : ''}`}>
                                    {formatDate(client.next_contact_date)}
                                    {isOverdue && <span className="block text-xs">({Math.abs(daysUntil)} días atrasado)</span>}
                                    {isDueToday && <span className="block text-xs">(Hoy)</span>}
                                  </div>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => sendClientWhatsApp(client)}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    title="WhatsApp"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => fetchClientDetail(client.id)}
                                    className="text-[#003DA5] hover:text-[#E31C23] hover:bg-blue-50"
                                    title="Ver detalle"
                                    data-testid={`view-client-${client.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="statistics" data-testid="statistics-tab-content">
            {statsLoading && !analyticsStats ? (
              // Skeleton (only when no cached data is available)
              <div className="space-y-6" data-testid="statistics-skeleton">
                <div className="grid md:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Card key={`stat-skel-${i}`} className="animate-pulse">
                      <CardHeader className="pb-2">
                        <div className="h-3 bg-slate-200 rounded w-2/3" />
                      </CardHeader>
                      <CardContent>
                        <div className="h-8 bg-slate-200 rounded w-1/2 mb-2" />
                        <div className="h-3 bg-slate-200 rounded w-3/4" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="animate-pulse">
                  <CardHeader>
                    <div className="h-5 bg-slate-200 rounded w-1/3" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-40 bg-slate-200 rounded" />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <>
                {statsLoading && (
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-500 mb-4" data-testid="statistics-refreshing-indicator">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#003DA5]"></div>
                    <span>Actualizando estadísticas...</span>
                  </div>
                )}
                {/* Summary Cards */}
                <div className="grid md:grid-cols-4 gap-4 mb-8">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Visitantes Hoy</CardTitle>
                      <Users className="h-4 w-4 text-[#003DA5]" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-[#003DA5]">
                        {analyticsStats?.today?.visitors || 0}
                      </div>
                      <p className="text-xs text-slate-500">
                        {analyticsStats?.today?.events || 0} eventos
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Esta Semana</CardTitle>
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        {analyticsStats?.this_week?.visitors || 0}
                      </div>
                      <p className="text-xs text-slate-500">
                        visitantes únicos
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Este Mes</CardTitle>
                      <BarChart3 className="h-4 w-4 text-purple-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-purple-600">
                        {analyticsStats?.this_month?.visitors || 0}
                      </div>
                      <p className="text-xs text-slate-500">
                        {analyticsStats?.this_month?.events || 0} eventos totales
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Ayer</CardTitle>
                      <Clock className="h-4 w-4 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-orange-600">
                        {analyticsStats?.yesterday?.visitors || 0}
                      </div>
                      <p className="text-xs text-slate-500">
                        visitantes
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Monthly Activity Breakdown */}
                {analyticsStats?.this_month?.breakdown && (
                  <Card className="mb-8">
                    <CardHeader>
                      <CardTitle className="text-lg">Actividad del Mes</CardTitle>
                      <CardDescription>Desglose de eventos en los últimos 30 días</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg text-center">
                          <MousePointer className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                          <div className="text-2xl font-bold text-blue-600">
                            {analyticsStats.this_month.breakdown.page_view || 0}
                          </div>
                          <div className="text-xs text-slate-600">Visitas a páginas</div>
                        </div>
                        
                        <div className="bg-green-50 p-4 rounded-lg text-center">
                          <Search className="w-6 h-6 mx-auto mb-2 text-green-600" />
                          <div className="text-2xl font-bold text-green-600">
                            {analyticsStats.this_month.breakdown.search || 0}
                          </div>
                          <div className="text-xs text-slate-600">Búsquedas</div>
                        </div>
                        
                        <div className="bg-purple-50 p-4 rounded-lg text-center">
                          <Calculator className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                          <div className="text-2xl font-bold text-purple-600">
                            {analyticsStats.this_month.breakdown.calculator_use || 0}
                          </div>
                          <div className="text-xs text-slate-600">Uso calculadora</div>
                        </div>
                        
                        <div className="bg-red-50 p-4 rounded-lg text-center">
                          <svg className="w-6 h-6 mx-auto mb-2 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                          <div className="text-2xl font-bold text-red-600">
                            {analyticsStats.this_month.breakdown.favorite_add || 0}
                          </div>
                          <div className="text-xs text-slate-600">Favoritos</div>
                        </div>
                        
                        <div className="bg-yellow-50 p-4 rounded-lg text-center">
                          <Calendar className="w-6 h-6 mx-auto mb-2 text-yellow-600" />
                          <div className="text-2xl font-bold text-yellow-600">
                            {analyticsStats.this_month.breakdown.schedule_visit || 0}
                          </div>
                          <div className="text-xs text-slate-600">Visitas agendadas</div>
                        </div>
                        
                        <div className="bg-teal-50 p-4 rounded-lg text-center">
                          <Phone className="w-6 h-6 mx-auto mb-2 text-teal-600" />
                          <div className="text-2xl font-bold text-teal-600">
                            {analyticsStats.this_month.breakdown.contact_click || 0}
                          </div>
                          <div className="text-xs text-slate-600">Clics contacto</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Top Searched Zones */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Barrios más Buscados</CardTitle>
                      <CardDescription>Últimos 30 días</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {searchStats.top_zones.length > 0 ? (
                        <div className="space-y-3">
                          {searchStats.top_zones.slice(0, 10).map((zone, index) => (
                            <div key={zone.zone} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-slate-500 w-6">{index + 1}.</span>
                                <span className="font-medium">{zone.zone}</span>
                              </div>
                              <Badge variant="secondary">{zone.searches} búsquedas</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No hay datos de búsquedas aún</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top Pages */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Páginas más Visitadas</CardTitle>
                      <CardDescription>Últimos 30 días</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {pageStats.length > 0 ? (
                        <div className="space-y-3">
                          {pageStats.slice(0, 10).map((page, index) => (
                            <div key={page.page} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-slate-500 w-6">{index + 1}.</span>
                                <span className="font-medium">{page.page || 'Inicio'}</span>
                              </div>
                              <div className="text-right">
                                <Badge variant="secondary">{page.views} visitas</Badge>
                                <span className="text-xs text-slate-500 ml-2">({page.unique_visitors} únicos)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No hay datos de páginas aún</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Stats Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Estadísticas Diarias</CardTitle>
                    <CardDescription>Últimos 30 días</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dailyStats.length > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Fecha</TableHead>
                              <TableHead className="text-center">Visitantes</TableHead>
                              <TableHead className="text-center">Visitas</TableHead>
                              <TableHead className="text-center">Búsquedas</TableHead>
                              <TableHead className="text-center">Calculadora</TableHead>
                              <TableHead className="text-center">Favoritos</TableHead>
                              <TableHead className="text-center">Visitas Agendadas</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {dailyStats.map((day) => (
                              <TableRow key={day.date}>
                                <TableCell className="font-medium">{day.date}</TableCell>
                                <TableCell className="text-center">{day.unique_visitors}</TableCell>
                                <TableCell className="text-center">{day.page_views}</TableCell>
                                <TableCell className="text-center">{day.searches}</TableCell>
                                <TableCell className="text-center">{day.calculator_uses}</TableCell>
                                <TableCell className="text-center">{day.favorites_added}</TableCell>
                                <TableCell className="text-center">{day.visits_scheduled}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-8">
                        No hay datos de estadísticas aún. Los datos se irán acumulando a medida que los usuarios visiten el sitio.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" data-testid="subscriptions-tab-content">
            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Suscripciones</CardTitle>
                  <Bell className="h-4 w-4 text-[#003DA5]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#003DA5]">{subscriptions.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Alertas de Propiedades</CardTitle>
                  <Bell className="h-4 w-4 text-[#E31C23]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#E31C23]">
                    {subscriptions.filter(s => s.subscription_type === 'alerts').length}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Descargas Checklist</CardTitle>
                  <Download className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {subscriptions.filter(s => s.subscription_type === 'checklist').length}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filter */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                  <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
                    <SelectTrigger className="w-full md:w-64">
                      <SelectValue placeholder="Filtrar por tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las suscripciones</SelectItem>
                      <SelectItem value="alerts">Alertas de propiedades</SelectItem>
                      <SelectItem value="checklist">Descargas de checklist</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchSubscriptions} variant="outline">
                    Actualizar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Subscriptions Table */}
            <Card>
              <CardHeader>
                <CardTitle>Lista de Suscripciones</CardTitle>
                <CardDescription>
                  {getFilteredSubscriptions().length} suscripciones
                </CardDescription>
              </CardHeader>
              <CardContent>
                {getFilteredSubscriptions().length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No hay suscripciones para mostrar</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Detalles</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getFilteredSubscriptions().map((sub) => (
                          <TableRow key={sub.id}>
                            <TableCell>
                              {sub.subscription_type === 'alerts' ? (
                                <Badge className="bg-red-100 text-red-800">
                                  <Bell className="w-3 h-3 mr-1" />
                                  Alerta
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800">
                                  <Download className="w-3 h-3 mr-1" />
                                  Checklist
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{sub.email}</TableCell>
                            <TableCell>{sub.name || '-'}</TableCell>
                            <TableCell>
                              {sub.subscription_type === 'alerts' ? (
                                <div className="text-sm space-y-1">
                                  {sub.zones && <div><span className="text-slate-500">Zona:</span> {sub.zones}</div>}
                                  {sub.interest && <div><span className="text-slate-500">Interés:</span> {sub.interest}</div>}
                                  {sub.property_types && <div><span className="text-slate-500">Tipo:</span> {sub.property_types}</div>}
                                  {(sub.budget_min || sub.budget_max) && (
                                    <div><span className="text-slate-500">Presupuesto:</span> USD {sub.budget_min || '?'} - {sub.budget_max || '?'}</div>
                                  )}
                                  {(sub.rooms_min || sub.rooms_max) && (
                                    <div><span className="text-slate-500">Ambientes:</span> {sub.rooms_min || '?'} - {sub.rooms_max || '?'}</div>
                                  )}
                                  {(sub.size_min || sub.size_max) && (
                                    <div><span className="text-slate-500">m²:</span> {sub.size_min || '?'} - {sub.size_max || '?'}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-500">Descargó checklist</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {sub.created_at ? new Date(sub.created_at).toLocaleDateString('es-AR') : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const message = `Hola ${sub.name || ''}, te contactamos desde Vendetucasa.ar. Vimos que estás buscando propiedades. ¿Te podemos ayudar?`;
                                    window.open(`mailto:${sub.email}?subject=Propiedades que te pueden interesar&body=${encodeURIComponent(message)}`, '_blank');
                                  }}
                                  className="text-[#003DA5] hover:text-[#003DA5] hover:bg-blue-50"
                                  title="Enviar email"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteSubscription(sub.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appraisals Tab */}
          <TabsContent value="appraisals" data-testid="appraisals-tab-content">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Solicitudes</CardTitle>
                  <HomeIcon className="h-4 w-4 text-[#003DA5]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#003DA5]">{appraisals.length}</div>
                  <p className="text-xs text-slate-600">Todas las solicitudes recibidas</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
                  <Calendar className="h-4 w-4 text-[#E31C23]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#E31C23]">
                    {appraisals.filter(a => a.status === 'pending').length}
                  </div>
                  <p className="text-xs text-slate-600">Sin responder</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Contactadas</CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {appraisals.filter(a => a.status === 'contacted').length}
                  </div>
                  <p className="text-xs text-slate-600">Ya respondidas</p>
                </CardContent>
              </Card>
            </div>

            {/* Actions Bar */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                  <div className="relative flex-1 w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      placeholder="Buscar por nombre, email, teléfono o dirección..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={fetchAppraisals} variant="outline">
                      Actualizar
                    </Button>
                    <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700 text-white">
                      <Download className="w-4 h-4 mr-2" />
                      Exportar CSV
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardHeader>
                <CardTitle>Solicitudes de Tasación</CardTitle>
                <CardDescription>
                  {filteredAppraisals.length} de {appraisals.length} solicitudes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E31C23]"></div>
                    <p className="mt-4 text-slate-600">Cargando solicitudes...</p>
                  </div>
                ) : filteredAppraisals.length === 0 ? (
                  <div className="text-center py-12">
                    <HomeIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No hay solicitudes para mostrar</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Contacto</TableHead>
                          <TableHead>Propiedad</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAppraisals.map((appraisal) => (
                          <TableRow key={appraisal.id}>
                            <TableCell className="font-medium">
                              {new Date(appraisal.createdAt).toLocaleDateString('es-AR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                              <br />
                              <span className="text-xs text-slate-500">
                                {new Date(appraisal.createdAt).toLocaleTimeString('es-AR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{appraisal.name}</div>
                              <div className="text-sm text-slate-500">{appraisal.email}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">{appraisal.phone}</div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{getPropertyTypeLabel(appraisal.propertyType)}</div>
                              <div className="text-sm text-slate-500">
                                {appraisal.size ? `${appraisal.size}m²` : '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              {appraisal.status === 'pending' ? (
                                <Badge variant="outline" className="border-[#E31C23] text-[#E31C23]">
                                  Pendiente
                                </Badge>
                              ) : appraisal.status === 'contacted' ? (
                                <Badge variant="outline" className="border-green-600 text-green-600">
                                  Contactada
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-slate-400 text-slate-400">
                                  Cerrada
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => sendWhatsApp(appraisal)}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title="Enviar WhatsApp"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => viewDetail(appraisal)}
                                  className="text-[#003DA5] hover:text-[#E31C23] hover:bg-blue-50"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Articles Tab */}
          <TabsContent value="articles" data-testid="articles-tab-content">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Create Article Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Plus className="w-5 h-5 mr-2 text-[#E31C23]" />
                    Crear Nuevo Artículo
                  </CardTitle>
                  <CardDescription>Comparte noticias e información útil</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createArticle} className="space-y-4">
                    <div>
                      <Label htmlFor="title">Título *</Label>
                      <Input
                        id="title"
                        value={newArticle.title}
                        onChange={(e) => setNewArticle({...newArticle, title: e.target.value})}
                        placeholder="Título del artículo"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="excerpt">Resumen (opcional)</Label>
                      <Textarea
                        id="excerpt"
                        value={newArticle.excerpt}
                        onChange={(e) => setNewArticle({...newArticle, excerpt: e.target.value})}
                        placeholder="Breve descripción del artículo"
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="article-content">Contenido *</Label>
                      <div className="mt-1 border rounded-lg overflow-hidden">
                        <div className="bg-slate-50 border-b px-2 py-2 flex flex-wrap gap-1">
                          <Button type="button" size="sm" variant="outline" onClick={() => formatText('bold')} title="Negrita">
                            <strong>B</strong>
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => formatText('italic')} title="Cursiva">
                            <em>I</em>
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => formatText('h1')} title="Título 1">
                            H1
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => formatText('h2')} title="Título 2">
                            H2
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => formatText('h3')} title="Título 3">
                            H3
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => formatText('p')} title="Párrafo">
                            P
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => formatText('link')} title="Link">
                            Link
                          </Button>
                        </div>
                        <Textarea
                          id="article-content"
                          value={newArticle.content}
                          onChange={(e) => setNewArticle({...newArticle, content: e.target.value})}
                          placeholder="Escribe el contenido del artículo aquí..."
                          required
                          rows={8}
                          className="border-0 rounded-none focus-visible:ring-0"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Selecciona texto y usa los botones para aplicar formato</p>
                    </div>
                    <div>
                      <Label htmlFor="image_url">URL de Imagen (opcional)</Label>
                      <Input
                        id="image_url"
                        type="url"
                        value={newArticle.image_url}
                        onChange={(e) => setNewArticle({...newArticle, image_url: e.target.value})}
                        placeholder="https://ejemplo.com/imagen.jpg"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Puedes usar URLs de Unsplash o similar</p>
                    </div>
                    <Button type="submit" className="w-full bg-[#E31C23] hover:bg-[#C11A1F] text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Publicar Artículo
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Articles List */}
              <Card>
                <CardHeader>
                  <CardTitle>Artículos Publicados</CardTitle>
                  <CardDescription>{articles.length} artículos en total</CardDescription>
                </CardHeader>
                <CardContent>
                  {articles.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600">No hay artículos publicados</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto">
                      {articles.map((article) => (
                        <Card key={article.id} className="border">
                          <CardContent className="pt-6">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1">
                                {article.image_url && (
                                  <img 
                                    src={article.image_url} 
                                    alt={article.title}
                                    className="w-full h-32 object-cover rounded-lg mb-3"
                                  />
                                )}
                                <h3 className="font-semibold text-[#003DA5] mb-2">{article.title}</h3>
                                <div 
                                  className="text-sm text-slate-600 line-clamp-2 mb-2 prose prose-sm"
                                  dangerouslySetInnerHTML={{ 
                                    __html: article.excerpt || article.content 
                                  }}
                                />
                                <p className="text-xs text-slate-500">
                                  {new Date(article.created_at).toLocaleDateString('es-AR', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })}
                                </p>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => viewArticleDetail(article)}
                                  className="text-[#003DA5] hover:text-[#E31C23] hover:bg-blue-50"
                                  title="Ver artículo"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startEditArticle(article)}
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  title="Editar artículo"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteArticle(article.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Eliminar artículo"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Client Dialog */}
      <Dialog open={showCreateClientDialog} onOpenChange={setShowCreateClientDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#003DA5] flex items-center">
              <UserPlus className="w-6 h-6 mr-2 text-[#E31C23]" />
              Nuevo Cliente
            </DialogTitle>
            <DialogDescription>
              Completa los datos del nuevo cliente
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createClient} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client-name">Nombre *</Label>
                <Input
                  id="client-name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                  placeholder="Nombre completo"
                  required
                  className="mt-1"
                  data-testid="new-client-name"
                />
              </div>
              <div>
                <Label htmlFor="client-phone">Teléfono</Label>
                <Input
                  id="client-phone"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                  placeholder="549..."
                  className="mt-1"
                  data-testid="new-client-phone"
                />
              </div>
              <div>
                <Label htmlFor="client-email">Email</Label>
                <Input
                  id="client-email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                  placeholder="email@ejemplo.com"
                  className="mt-1"
                  data-testid="new-client-email"
                />
              </div>
              <div>
                <Label htmlFor="client-interest">Interés</Label>
                <Select value={newClient.interest} onValueChange={(v) => setNewClient({...newClient, interest: v})}>
                  <SelectTrigger className="mt-1" data-testid="new-client-interest">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_INTEREST_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="client-property-type">Tipo de Propiedad</Label>
                <Select value={newClient.property_type} onValueChange={(v) => setNewClient({...newClient, property_type: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casa">Casa</SelectItem>
                    <SelectItem value="departamento">Departamento</SelectItem>
                    <SelectItem value="terreno">Terreno</SelectItem>
                    <SelectItem value="local">Local Comercial</SelectItem>
                    <SelectItem value="oficina">Oficina</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="client-neighborhood">Barrio/Zona</Label>
                <Input
                  id="client-neighborhood"
                  value={newClient.neighborhood}
                  onChange={(e) => setNewClient({...newClient, neighborhood: e.target.value})}
                  placeholder="Ej: Palermo, Belgrano..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="client-budget-min">Presupuesto Mín (USD)</Label>
                <Input
                  id="client-budget-min"
                  type="number"
                  value={newClient.budget_min}
                  onChange={(e) => setNewClient({...newClient, budget_min: e.target.value})}
                  placeholder="50000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="client-budget-max">Presupuesto Máx (USD)</Label>
                <Input
                  id="client-budget-max"
                  type="number"
                  value={newClient.budget_max}
                  onChange={(e) => setNewClient({...newClient, budget_max: e.target.value})}
                  placeholder="150000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="client-size">Superficie Deseada</Label>
                <Input
                  id="client-size"
                  value={newClient.property_size}
                  onChange={(e) => setNewClient({...newClient, property_size: e.target.value})}
                  placeholder="Ej: 80-100 m²"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="client-followup">Frecuencia de Seguimiento</Label>
                <Select value={newClient.follow_up_frequency} onValueChange={(v) => setNewClient({...newClient, follow_up_frequency: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLLOW_UP_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="client-notes">Notas</Label>
              <Textarea
                id="client-notes"
                value={newClient.notes}
                onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                placeholder="Información adicional sobre el cliente..."
                rows={3}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-[#E31C23] hover:bg-[#C11A1F] text-white" data-testid="save-new-client-button">
                <UserPlus className="w-4 h-4 mr-2" />
                Guardar Cliente
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreateClientDialog(false)} className="flex-1">
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={showClientDetailDialog} onOpenChange={setShowClientDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedClient && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-[#003DA5] flex items-center justify-between">
                  <span className="flex items-center">
                    <Users className="w-6 h-6 mr-2" />
                    {selectedClient.name}
                  </span>
                  {getStatusBadge(selectedClient.status)}
                </DialogTitle>
                <DialogDescription>
                  Cliente desde {formatDate(selectedClient.created_at)} | Fuente: {selectedClient.source || 'Manual'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Contact Info */}
                <div>
                  <h3 className="font-semibold text-[#003DA5] mb-3">Información de Contacto</h3>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-slate-500 mr-2" />
                      <span>{selectedClient.phone || 'Sin teléfono'}</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-slate-500 mr-2" />
                      <span>{selectedClient.email || 'Sin email'}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-slate-500 mr-2" />
                      <span>{selectedClient.neighborhood || 'Sin barrio'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Property Interest */}
                <div>
                  <h3 className="font-semibold text-[#003DA5] mb-3">Interés Inmobiliario</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Interés:</strong> {CLIENT_INTEREST_OPTIONS.find(i => i.value === selectedClient.interest)?.label || selectedClient.interest}</p>
                    <p><strong>Tipo:</strong> {getPropertyTypeLabel(selectedClient.property_type) || '-'}</p>
                    <p><strong>Superficie:</strong> {selectedClient.property_size || '-'}</p>
                    {(selectedClient.budget_min || selectedClient.budget_max) && (
                      <p><strong>Presupuesto:</strong> USD {selectedClient.budget_min?.toLocaleString() || '?'} - {selectedClient.budget_max?.toLocaleString() || '?'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Follow-up Info */}
              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <h3 className="font-semibold text-[#003DA5] mb-2">Seguimiento</h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Frecuencia</p>
                    <p className="font-medium">{FOLLOW_UP_OPTIONS.find(f => f.value === selectedClient.follow_up_frequency)?.label || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Último contacto</p>
                    <p className="font-medium">{formatDate(selectedClient.last_contact_date) || 'Nunca'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Próximo contacto</p>
                    <p className={`font-medium ${getDaysUntilContact(selectedClient.next_contact_date) < 0 ? 'text-red-600' : ''}`}>
                      {formatDate(selectedClient.next_contact_date) || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedClient.notes && (
                <div className="mt-4">
                  <h3 className="font-semibold text-[#003DA5] mb-2">Notas</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedClient.notes}</p>
                </div>
              )}

              {/* Interactions History */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-[#003DA5] flex items-center">
                    <History className="w-4 h-4 mr-2" />
                    Historial de Interacciones
                  </h3>
                  <Button size="sm" onClick={() => setShowAddInteractionDialog(true)} className="bg-[#003DA5]">
                    <Plus className="w-4 h-4 mr-1" /> Registrar
                  </Button>
                </div>
                {selectedClient.interactions && selectedClient.interactions.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedClient.interactions.map(interaction => (
                      <div key={interaction.id} className="bg-slate-50 p-3 rounded-lg text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <Badge variant="outline" className="mr-2">
                              {INTERACTION_TYPES.find(t => t.value === interaction.type)?.label || interaction.type}
                            </Badge>
                            {interaction.outcome && (
                              <Badge variant="secondary">
                                {INTERACTION_OUTCOMES.find(o => o.value === interaction.outcome)?.label || interaction.outcome}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-slate-500">{formatDate(interaction.created_at)}</span>
                        </div>
                        {interaction.notes && <p className="mt-2 text-slate-700">{interaction.notes}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">Sin interacciones registradas</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
                <Button onClick={() => sendClientWhatsApp(selectedClient)} className="bg-green-600 hover:bg-green-700">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
                <Button onClick={() => markAsContacted(selectedClient.id)} variant="outline" className="border-blue-600 text-blue-600">
                  <CheckCircle className="w-4 h-4 mr-2" /> Marcar Contactado
                </Button>
                <Button onClick={() => { setShowClientDetailDialog(false); setShowEditClientDialog(true); }} variant="outline" className="border-amber-600 text-amber-600">
                  <Pencil className="w-4 h-4 mr-2" /> Editar
                </Button>
                <Button onClick={() => deleteClient(selectedClient.id)} variant="outline" className="border-red-600 text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditClientDialog} onOpenChange={setShowEditClientDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#003DA5] flex items-center">
              <Pencil className="w-6 h-6 mr-2 text-amber-600" />
              Editar Cliente
            </DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <form onSubmit={updateClient} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-client-name">Nombre *</Label>
                  <Input
                    id="edit-client-name"
                    value={selectedClient.name}
                    onChange={(e) => setSelectedClient({...selectedClient, name: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-client-phone">Teléfono</Label>
                  <Input
                    id="edit-client-phone"
                    value={selectedClient.phone || ''}
                    onChange={(e) => setSelectedClient({...selectedClient, phone: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-client-email">Email</Label>
                  <Input
                    id="edit-client-email"
                    type="email"
                    value={selectedClient.email || ''}
                    onChange={(e) => setSelectedClient({...selectedClient, email: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={selectedClient.status} onValueChange={(v) => setSelectedClient({...selectedClient, status: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Interés</Label>
                  <Select value={selectedClient.interest} onValueChange={(v) => setSelectedClient({...selectedClient, interest: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_INTEREST_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de Propiedad</Label>
                  <Select value={selectedClient.property_type || ''} onValueChange={(v) => setSelectedClient({...selectedClient, property_type: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casa">Casa</SelectItem>
                      <SelectItem value="departamento">Departamento</SelectItem>
                      <SelectItem value="terreno">Terreno</SelectItem>
                      <SelectItem value="local">Local Comercial</SelectItem>
                      <SelectItem value="oficina">Oficina</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-client-neighborhood">Barrio/Zona</Label>
                  <Input
                    id="edit-client-neighborhood"
                    value={selectedClient.neighborhood || ''}
                    onChange={(e) => setSelectedClient({...selectedClient, neighborhood: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Frecuencia de Seguimiento</Label>
                  <Select value={selectedClient.follow_up_frequency} onValueChange={(v) => setSelectedClient({...selectedClient, follow_up_frequency: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOW_UP_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-client-budget-min">Presupuesto Mín (USD)</Label>
                  <Input
                    id="edit-client-budget-min"
                    type="number"
                    value={selectedClient.budget_min || ''}
                    onChange={(e) => setSelectedClient({...selectedClient, budget_min: e.target.value ? parseFloat(e.target.value) : null})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-client-budget-max">Presupuesto Máx (USD)</Label>
                  <Input
                    id="edit-client-budget-max"
                    type="number"
                    value={selectedClient.budget_max || ''}
                    onChange={(e) => setSelectedClient({...selectedClient, budget_max: e.target.value ? parseFloat(e.target.value) : null})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-client-notes">Notas</Label>
                <Textarea
                  id="edit-client-notes"
                  value={selectedClient.notes || ''}
                  onChange={(e) => setSelectedClient({...selectedClient, notes: e.target.value})}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-[#E31C23] hover:bg-[#C11A1F] text-white">
                  <CheckCircle className="w-4 h-4 mr-2" /> Guardar Cambios
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditClientDialog(false)} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Interaction Dialog */}
      <Dialog open={showAddInteractionDialog} onOpenChange={setShowAddInteractionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#003DA5] flex items-center">
              <History className="w-5 h-5 mr-2" />
              Registrar Interacción
            </DialogTitle>
            <DialogDescription>
              {selectedClient && `Con ${selectedClient.name}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={addInteraction} className="space-y-4">
            <div>
              <Label>Tipo de Interacción</Label>
              <Select value={newInteraction.type} onValueChange={(v) => setNewInteraction({...newInteraction, type: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERACTION_TYPES.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Resultado</Label>
              <Select value={newInteraction.outcome} onValueChange={(v) => setNewInteraction({...newInteraction, outcome: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERACTION_OUTCOMES.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="interaction-notes">Notas</Label>
              <Textarea
                id="interaction-notes"
                value={newInteraction.notes}
                onChange={(e) => setNewInteraction({...newInteraction, notes: e.target.value})}
                placeholder="Detalles de la conversación..."
                rows={3}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-[#003DA5] hover:bg-[#002d7a] text-white">
                <Plus className="w-4 h-4 mr-2" /> Registrar
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddInteractionDialog(false)} className="flex-1">
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Article Preview Dialog */}
      <Dialog open={showArticleDetail} onOpenChange={setShowArticleDetail}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedArticle && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-[#003DA5]">{selectedArticle.title}</DialogTitle>
                <DialogDescription>
                  {new Date(selectedArticle.created_at).toLocaleDateString('es-AR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </DialogDescription>
              </DialogHeader>
              {selectedArticle.image_url && (
                <img 
                  src={selectedArticle.image_url} 
                  alt={selectedArticle.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
              )}
              <div 
                className="prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Appraisal Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#003DA5]">Detalle de Solicitud</DialogTitle>
            <DialogDescription>
              Recibida el {selectedAppraisal && new Date(selectedAppraisal.createdAt).toLocaleDateString('es-AR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </DialogDescription>
          </DialogHeader>
          {selectedAppraisal && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#003DA5] font-semibold">Información del Cliente</Label>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-start">
                      <Mail className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                      <div>
                        <div className="text-sm text-slate-600">Email</div>
                        <div className="font-medium">{selectedAppraisal.email}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Phone className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                      <div>
                        <div className="text-sm text-slate-600">Teléfono</div>
                        <div className="font-medium">{selectedAppraisal.phone}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-[#003DA5] font-semibold">Datos de la Propiedad</Label>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-start">
                      <HomeIcon className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                      <div>
                        <div className="text-sm text-slate-600">Tipo</div>
                        <div className="font-medium">{getPropertyTypeLabel(selectedAppraisal.propertyType)}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MapPin className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                      <div>
                        <div className="text-sm text-slate-600">Dirección</div>
                        <div className="font-medium">{selectedAppraisal.address}</div>
                      </div>
                    </div>
                    {selectedAppraisal.size && (
                      <div>
                        <div className="text-sm text-slate-600">Superficie</div>
                        <div className="font-medium">{selectedAppraisal.size} m²</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedAppraisal.reason && (
                <div>
                  <Label className="text-[#003DA5] font-semibold">Motivo de la Tasación</Label>
                  <div className="mt-2 p-4 bg-slate-50 rounded-lg">
                    <p className="text-slate-700">{selectedAppraisal.reason}</p>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 pt-4">
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    onClick={() => sendWhatsApp(selectedAppraisal)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                  <Button asChild variant="outline" className="border-[#003DA5] text-[#003DA5]">
                    <a href={`tel:${selectedAppraisal.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Llamar
                    </a>
                  </Button>
                </div>
                <Button asChild variant="outline" className="w-full">
                  <a href={`mailto:${selectedAppraisal.email}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Enviar Email
                  </a>
                </Button>
                
                {selectedAppraisal.status === 'pending' && (
                  <Button 
                    onClick={() => updateStatus(selectedAppraisal.id, 'contacted')}
                    className="w-full bg-[#E31C23] hover:bg-[#C11A1F] mt-2"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Marcar como Contactada
                  </Button>
                )}
                
                {selectedAppraisal.status === 'contacted' && (
                  <Button 
                    onClick={() => updateStatus(selectedAppraisal.id, 'pending')}
                    variant="outline"
                    className="w-full mt-2"
                  >
                    Volver a Pendiente
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Article Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#003DA5] flex items-center">
              <Pencil className="w-5 h-5 mr-2 text-amber-600" />
              Editar Artículo
            </DialogTitle>
            <DialogDescription>
              Modifica los campos del artículo y guarda los cambios
            </DialogDescription>
          </DialogHeader>
          {editingArticle && (
            <form onSubmit={updateArticle} className="space-y-4">
              <div>
                <Label htmlFor="edit-title">Título *</Label>
                <Input
                  id="edit-title"
                  value={editingArticle.title}
                  onChange={(e) => setEditingArticle({...editingArticle, title: e.target.value})}
                  placeholder="Título del artículo"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-excerpt">Resumen (opcional)</Label>
                <Textarea
                  id="edit-excerpt"
                  value={editingArticle.excerpt}
                  onChange={(e) => setEditingArticle({...editingArticle, excerpt: e.target.value})}
                  placeholder="Breve descripción del artículo"
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-article-content">Contenido *</Label>
                <div className="mt-1 border rounded-lg overflow-hidden">
                  <div className="bg-slate-50 border-b px-2 py-2 flex flex-wrap gap-1">
                    <Button type="button" size="sm" variant="outline" onClick={() => formatText('bold', true)} title="Negrita">
                      <strong>B</strong>
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => formatText('italic', true)} title="Cursiva">
                      <em>I</em>
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => formatText('h1', true)} title="Título 1">
                      H1
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => formatText('h2', true)} title="Título 2">
                      H2
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => formatText('h3', true)} title="Título 3">
                      H3
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => formatText('p', true)} title="Párrafo">
                      P
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => formatText('link', true)} title="Link">
                      Link
                    </Button>
                  </div>
                  <Textarea
                    id="edit-article-content"
                    value={editingArticle.content}
                    onChange={(e) => setEditingArticle({...editingArticle, content: e.target.value})}
                    placeholder="Escribe el contenido del artículo aquí..."
                    required
                    rows={10}
                    className="border-0 rounded-none focus-visible:ring-0"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Selecciona texto y usa los botones para aplicar formato</p>
              </div>
              <div>
                <Label htmlFor="edit-image_url">URL de Imagen (opcional)</Label>
                <Input
                  id="edit-image_url"
                  type="url"
                  value={editingArticle.image_url}
                  onChange={(e) => setEditingArticle({...editingArticle, image_url: e.target.value})}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-[#E31C23] hover:bg-[#C11A1F] text-white">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingArticle(null);
                  }}
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
